<template>
    <div class="relative">
        <label class="relative block w-full">
            <span
                class="block text-slate-600 text-sm font-medium leading-tight mb-2"
            >
                {{ label }}
            </span>
            <input
                type="text"
                v-model="airportsQuery"
                class="block w-full h-10 bg-white rounded border border-slate-200 py-1.5 text-gray-900 placeholder:text-slate-500 placeholder:text-sm transition-colors focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                :placeholder="placeholder"
                v-debounce="350"
                @input="searchAirports"
                @focus="updateFocus(true)"
                @blur="updateFocus(false)"
                @keydown.enter.prevent="enter()"
                @keydown.down="down"
                @keydown.up="up"
                autocomplete="off"
                v-if="!airportChosen"
            />
            <div
                class="w-full h-9 bg-white rounded border border-slate-200 py-1.5 pl-4 text-gray-900 placeholder:text-slate-500 placeholder:text-sm transition-colors focus:ring-0 focus:border-slate-800 hover:border-slate-800 uppercase"
                v-if="airportChosen"
            >
                {{ airportChosen }}
            </div>
            <button
                class="absolute right-2 top-[34px] z-10"
                @click="reset()"
                v-if="airportChosen"
            >
                <Icon icon="close" class="text-red-700" />
            </button>
        </label>
        <div
            class="absolute left-0 right-0 top-full z-10 pt-2"
            v-if="airportsQuery && airportsQuery.length > queryStartLength"
        >
            <ul
                id="airport-dropdown"
                class="block w-full max-h-40 min-h-16 overflow-y-auto p-2 pb-12 bg-white rounded-lg shadow relative"
            >
                <li
                    class="w-full flex items-center justify-center"
                    v-if="isSearching"
                >
                    <LoadingState :loading="true" />
                </li>
                <li
                    v-for="(airport, aIndex) in airports"
                    :key="`airport-${aIndex}`"
                    @click="selectAirport(airport)"
                    class="w-full cursor-pointer p-2 hover:bg-slate-100 transition-colors gap-2 rounded-md flex items-center justify-between"
                    :class="{ '!bg-slate-200': airport.active }"
                    role="button"
                >
                    <span class="text-sm">
                        <span class="font-bold uppercase">
                            {{ airport.code }}
                        </span>
                        -
                        {{ airport.name }}
                    </span>
                </li>
                <li
                    class="w-full p-4 gap-2 rounded-md flex"
                    v-if="!isSearching && airports?.length === 0"
                >
                    No results found, try adjusting your search.
                </li>
                <li
                    class="cursor-pointer p-2 bg-slate-50 hover:bg-slate-100 transition-colors gap-2 rounded-md flex items-center justify-between absolute left-1 right-1 bottom-1 text-blue-400"
                    role="button"
                    v-if="airportsQuery.length > 2"
                    @click="useQuery()"
                >
                    Use "{{ airportsQuery }}"
                </li>
            </ul>
        </div>
    </div>
</template>
<script setup>
import { ref, computed, watch } from 'vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    label: {
        type: String,
        required: true,
        default: 'Airport',
    },
    placeholder: {
        type: String,
        default: 'Search',
    },
    currentAirport: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['airportChanged']);

const airportsQuery = ref('');
const queryStartLength = ref(0);
const airports = ref([]);
const activeIndex = ref(-1);
const scrollAmount = ref(42);
const inputFocused = ref(false);
const isSearching = ref(false);
const airportChosen = ref(null);

watch(
    () => props.currentAirport,
    (newValue) => {
        if (newValue) {
            airportChosen.value = newValue;
        }
    },
    { immediate: true }
);

const scrollContainer = computed(() => {
    return document.getElementById('airport-dropdown');
});

const clearSearch = () => {
    airportsQuery.value = null;
    airports.value = [];
    isSearching.value = false;
};

const searchAirports = () => {
    isSearching.value = true;
    if (airportsQuery.value) {
        ApiService.apiCall(
            '/airports/search?search=' + airportsQuery.value,
            'get'
        )
            .then((response) => {
                airports.value = response.data;
                isSearching.value = false;
            })
            .catch((error) => {
                console.log(error);
                isSearching.value = false;
            });
    }
};

const selectAirport = (airport) => {
    airportChosen.value = airport.code.toUpperCase();
    emit('airportChanged', airport.code.toUpperCase());
    clearSearch();
};

const enter = () => {
    const airport = airports.value[activeIndex.value];
    airportChosen.value = airport.code.toUpperCase();
    emit('airportChanged', airport.code.toUpperCase());
    clearSearch();
};

const useQuery = () => {
    airportChosen.value = airportsQuery.value.toUpperCase();
    emit('airportChanged', airportsQuery.value);
    clearSearch();
};

const updateFocus = (val) => {
    inputFocused.value = val;
};

const resetActiveItem = () => {
    airports.value.forEach((item) => {
        item.active = false;
    });
};

const reset = () => {
    airportChosen.value = null;
    clearSearch();
    emit('airportChanged', null);
};

const up = () => {
    if (activeIndex.value >= 0) {
        if (activeIndex.value > 0) {
            activeIndex.value--;
        }

        resetActiveItem();
        airports.value[activeIndex.value].active = true;

        if (airports.value.length - activeIndex.value > 3) {
            scrollContainer.value.scrollTop =
                scrollContainer.value.scrollTop - scrollAmount.value;
        }
    }
};

const down = () => {
    if (
        activeIndex.value > -2 &&
        activeIndex.value + 1 < airports.value.length
    ) {
        activeIndex.value++;
        resetActiveItem();
        airports.value[activeIndex.value].active = true;

        if (activeIndex.value > 3) {
            scrollContainer.value.scrollTop =
                scrollAmount.value + scrollContainer.value.scrollTop;
        }
    }
};
</script>
